import { useMemo, useState } from "react";
import { Modal, Form, Button, InputGroup, Col } from "react-bootstrap";
import { useFormik } from "formik";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { useAuth } from "../../../hooks/useAuth";
import { startCase } from "lodash";
import { Popover } from "react-tiny-popover";
import ExpensesSelector from "../../utils/ExpensesSelector";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import {
  ACCOUNT_MAPPING_ENTITY_TYPES,
  ITEM_ACCOUNT_MAPPING,
  SYSTEM_WIDE_ACCOUNT_MAPPING,
  VENDOR_ACCOUNT_MAPPING,
  EMPLOYEE_ACCOUNT_MAPPING,
  CUSTOMER_ACCOUNT_MAPPING,
  PRODUCTION_ACCOUNT_MAPPING,
} from "../../../utils/globalNamespace";

/**
 * mode="modal" (default) — renders inside a Bootstrap Modal, driven by `show`/`onClose`.
 * mode="flat"            — renders the form inline wherever it's placed, no Modal wrapper.
 *                           `show`/`onClose` become optional; a Cancel button only
 *                           renders in flat mode if `onClose` is passed.
 */
function EntityAccountMapping({
  entityType = "",
  entityId = "",
  mode = "modal", // "modal" | "flat"
  show,
  onClose,
  onSuccess,
}) {
  const { backendUrl } = useAuth();
  const isFlat = mode === "flat";

  // Track which row's popover is open by index, instead of one shared boolean
  const [openPopoverIndex, setOpenPopoverIndex] = useState(null);

  const resolveFormFields = useMemo(() => {
    switch (entityType) {
      case ACCOUNT_MAPPING_ENTITY_TYPES.INVENTORY:
        return ITEM_ACCOUNT_MAPPING;
      case ACCOUNT_MAPPING_ENTITY_TYPES.SYSTEM_WIDE:
        return SYSTEM_WIDE_ACCOUNT_MAPPING;
      case ACCOUNT_MAPPING_ENTITY_TYPES.VENDOR:
        return VENDOR_ACCOUNT_MAPPING;
      case ACCOUNT_MAPPING_ENTITY_TYPES.EMPLOYEE:
        return EMPLOYEE_ACCOUNT_MAPPING;
      case ACCOUNT_MAPPING_ENTITY_TYPES.CUSTOMER:
        return CUSTOMER_ACCOUNT_MAPPING;
      case ACCOUNT_MAPPING_ENTITY_TYPES.PRODUCTION:
        return PRODUCTION_ACCOUNT_MAPPING;
      default:
        return {};
    }
  }, [entityType]);

  // In modal mode, only fetch once the modal is actually shown.
  // In flat mode there's no `show` gate — it's live as soon as it's mounted.
  const shouldFetch = isFlat ? true : !!show;

  // GET TO PREPOPULATE BY entityType, entityId
  const { data: existingMapping, isLoading: isMappingLoading } = useQuery(
    ["entity-account-mapping", entityType, entityId],
    async () => {
      const response = await fetch(
        `${backendUrl}/api/accounts/entity-account-mapping?entityType=${entityType}&entityId=${entityId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "Application/json",
          },
        }
      );

      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(errorBody.message || "Unable to fetch account mapping");
      }

      return response.json();
    },
    {
      enabled: shouldFetch && !!entityType && !!entityId,
      onError: error => {
        toast.error(
          `Unable to fetch account mapping: ${error?.message ||
            "Please try again"}`
        );
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      mapping: Object.values(resolveFormFields).map(({ actionType }) => {
        const existingRow = existingMapping?.mapping?.find(
          row => row.entityActionType === actionType
        );
        return {
          entityType,
          entityId,
          entityActionType: actionType,
          mappedAccountId: existingRow?.mappedAccountId || "",
          mappedAccountDescription: existingRow?.mappedAccountDescription || "",
        };
      }),
    },
    enableReinitialize: true,
    onSubmit: values => {
      saveEntityAccountMappingMutation.mutate({ mapping: values.mapping });
    },
  });

  const saveEntityAccountMapping = async payload => {
    let response = await fetch(
      `${backendUrl}/api/accounts/entity-account-mapping`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(errorBody.message || "Unable to save account mapping");
    }

    return response.json();
  };

  const saveEntityAccountMappingMutation = useMutation(
    saveEntityAccountMapping,
    {
      onSuccess: ({ message }) => {
        toast.success(message || "Account mapping saved successfully");
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      },
      onError: error => {
        toast.error(
          `Unable to save account mapping: ${error?.message ||
            "Please try again"}`
        );
      },
    }
  );

  const formBody = (
    <Form onSubmit={formik.handleSubmit}>
      {formik.values.mapping.map((row, index) => {
        const fieldMeta = Object.values(resolveFormFields).find(
          ({ actionType }) => actionType === row.entityActionType
        );

        return (
          <Form.Group className="mb-3" key={row.entityActionType}>
            {/* entityType - hidden, still a real form field */}
            <Form.Control
              type="hidden"
              name={`mapping.${index}.entityType`}
              value={row.entityType}
              onChange={formik.handleChange}
            />
            {/* entityId - hidden, still a real form field */}
            <Form.Control
              type="hidden"
              name={`mapping.${index}.entityId`}
              value={row.entityId}
              onChange={formik.handleChange}
            />
            {/* entityActionType - visible but locked, label-driven */}
            <Form.Label>{fieldMeta?.label}</Form.Label>
            <Form.Control
              type="hidden"
              name={`mapping.${index}.entityActionType`}
              value={row.entityActionType}
              onChange={formik.handleChange}
            />
            {/* mappedAccountId - the one field the user actually edits */}
            <InputGroup hasValidation>
              <Form.Control
                type="text"
                placeholder={`Select account for ${fieldMeta?.label}`}
                value={`${row.mappedAccountId ||
                  "_"} / ${row.mappedAccountDescription || "_"}`}
                onChange={() => {}}
                onBlur={() => {}}
                readOnly
              />

              <Popover
                isOpen={openPopoverIndex === index}
                onClickOutside={() => setOpenPopoverIndex(null)}
                content={() => (
                  <ExpensesSelector
                    handleSelectedExpense={account => {
                      formik.setFieldValue(
                        `mapping.${index}.mappedAccountId`,
                        account?.AccountID ?? ""
                      );
                      formik.setFieldValue(
                        `mapping.${index}.mappedAccountDescription`,
                        account?.Description ?? ""
                      );
                      setOpenPopoverIndex(null);
                    }}
                    usage={"chart of accounts"}
                    onHide={() => setOpenPopoverIndex(null)}
                  />
                )}
                position="bottom"
              >
                <InputGroup.Text
                  onClick={() =>
                    setOpenPopoverIndex(
                      openPopoverIndex === index ? null : index
                    )
                  }
                >
                  <MagnifyIcon />
                </InputGroup.Text>
              </Popover>
            </InputGroup>
          </Form.Group>
        );
      })}
    </Form>
  );

  const saveButton = (
    <Button
      variant="primary"
      onClick={formik.handleSubmit}
      disabled={saveEntityAccountMappingMutation.isLoading}
    >
      {saveEntityAccountMappingMutation.isLoading ? "Saving..." : "Save"}
    </Button>
  );

  // ---- FLAT MODE: no Modal wrapper, just the form + inline actions ----
  if (isFlat) {
    return (
      <div className="entity-account-mapping-flat">
        {isMappingLoading ? <div>Loading account mapping...</div> : formBody}
        <div className="d-flex justify-content-end gap-2 mt-3">
          {onClose && (
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          )}
          {saveButton}
        </div>
      </div>
    );
  }

  // ---- MODAL MODE (default): original behavior ----
  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="md"
      animation={false}
      autoFocus={false}
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="h5">{entityId} Account Mapping</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isMappingLoading ? <div>Loading account mapping...</div> : formBody}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {saveButton}
      </Modal.Footer>
    </Modal>
  );
}

export default EntityAccountMapping;
